import React, { Suspense, lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useParentMessageListener } from 'src/hooks/useParentMessageListener';
import { XPTGlobalTopNavigation } from 'src/features/top-navigation/XptGlobalTopNavigation';
import AuthWrapper from 'src/features/xpt-layout/AuthWrapper';
import { XPTFooter } from 'src/features/xpt-layout/XPTFooter';
import XptHomePage from 'src/features/xpt-layout/XptHomePage';
import { ErrorFallback } from '../components/common/ErrorFallback';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { PageNotFound } from '../components/common/PageNotFound';
import { XptApplicationLevelBanner } from './AppBanner';
import { XPTBusinessGroupWrapper, XptFinanceAdminOrFinanceOwnerWrapper, XptXvtAccessWrapper } from './XPTBusinessGroupWrapper';

// -------------------------------------------------------------------------
// Lazy-loaded route components — code-split into separate chunks.
// These are NOT needed for the homepage and will only be downloaded
// when the user navigates to the corresponding route.
// -------------------------------------------------------------------------

// Access request routes
const RequestAccessPage = lazy(() => import('src/features/access-request/RequestAccessPage'));
const MyRequestsPage = lazy(() => import('src/features/access-request/MyRequestsPage'));
const AccessRequestFeatureGate = lazy(() => import('src/features/access-request/AccessRequestFeatureGate'));

// Business Group routes
const ForecastTemplateMainPage = lazy(() => import('src/features/business-group/forecast-template/ForecastTemplate'));
const AccessAuthorization = lazy(() =>
  import('src/features/business-group/access-authorization/AccessAuthorization').then((m) => ({ default: m.AccessAuthorization }))
);
const PlanningCycleHomePageNonAdmin = lazy(() => import('src/features/planning-cycle/PlanningCycleHomePageNonAdmin'));
const SnapshotsHomePage = lazy(() => import('src/features/business-group/planning-cycle-snapshot/SnapshotHomePage'));
const BudgetOwnerTransfer = lazy(() => import('src/features/business-group/budget-owner-transfer/BudgetOwnerTransfer'));
const ExpenseTypeMapping = lazy(() =>
  import('src/features/business-group/expense-type-mapping/ExpenseTypeMapping').then((m) => ({ default: m.ExpenseTypeMapping }))
);
const UsefulLifeMapping = lazy(() =>
  import('src/features/business-group/useful-life-mapping/UsefulLifeMapping').then((m) => ({ default: m.UsefulLifeMapping }))
);
const POTagging = lazy(() => import('src/features/po-tagging/POTagging').then((m) => ({ default: m.POTagging })));
const CampaignsHomePage = lazy(() =>
  import('src/features/business-group/campaigns/CampaignsHomePage').then((m) => ({ default: m.CampaignsHomePage }))
);
const DmmInvoiceMappingHomePage = lazy(() =>
  import('src/features/business-group/dmm-invoice-mapping/DmmInvoiceMappingHomePage').then((m) => ({ default: m.DmmInvoiceMappingHomePage }))
);
const DmmChannelFunnelMappingHomePage = lazy(() =>
  import('src/features/business-group/dmm-channel-funnel-mapping/DmmChannelFunnelMappingHomePage').then((m) => ({
    default: m.DmmChannelFunnelMappingHomePage
  }))
);
const XvtForecastInputMainPage = lazy(() => import('src/features/business-group/xvt-planning/XvtForecastInput'));
const XvtSnapshotsHomePage = lazy(() => import('src/features/business-group/xvt-planning/XvtSnapshotsHomePage'));
const XvtReportsHomePage = lazy(() => import('src/features/business-group/xvt-planning/XvtReportsHomePage'));

// Report routes
const XptForecastReport = lazy(() =>
  import('src/features/xpt-reports/forecast-report/XptForecastReport').then((m) => ({ default: m.XptForecastReport }))
);
const XptVarianceReport = lazy(() =>
  import('src/features/xpt-reports/variance-report/XptVarianceReport').then((m) => ({ default: m.XptVarianceReport }))
);
const ActualsVsForecastReport = lazy(() =>
  import('src/features/xpt-reports/actuals-vs-forecast-report/ActualsVsForecastReport').then((m) => ({ default: m.ActualsVsForecastReport }))
);
const XptActualsReport = lazy(() =>
  import('src/features/xpt-reports/actuals-report/XptActualsReport').then((m) => ({ default: m.XptActualsReport }))
);
const DmmMarketingSpend = lazy(() => import('src/features/xpt-reports/dmm-marketing-spend/DmmMarketingSpend'));
const RemainingBudgetReport = lazy(() =>
  import('src/features/xpt-reports/remaining-budget-report/RemainingBudgetReport').then((m) => ({ default: m.RemainingBudgetReport }))
);
const XptReportBuilder = lazy(() =>
  import('src/features/xpt-reports/report-builder/XptReportBuilder').then((m) => ({ default: m.XptReportBuilder }))
);

// Admin Console routes
const PlanningCycleHomePageAdmin = lazy(() => import('src/features/planning-cycle/PlanningCycleHomePageAdmin'));
const PlanningCycleManageFormAdmin = lazy(() => import('src/features/planning-cycle/admin-form'));
const ProjectMapping = lazy(() => import('src/features/admin-console/project-mapping/ProjectMapping').then((m) => ({ default: m.ProjectMapping })));
const CapexFileUpload = lazy(() => import('src/features/admin-console/capex/CapexFileUpload').then((m) => ({ default: m.CapexFileUpload })));
const OnboardingHomePage = lazy(() => import('src/features/admin-console/dev-console/onboarding-business-groups/OnboardingHomePage'));
const OnboardingForm = lazy(() => import('src/features/admin-console/dev-console/onboarding-business-groups/OnboardingForm'));
const AuditLog = lazy(() => import('src/features/admin-console/dev-console/audit-log/AuditLog').then((m) => ({ default: m.AuditLog })));
const UserAccessReport = lazy(() =>
  import('src/features/admin-console/dev-console/user-access-report/UserAccessReport').then((m) => ({ default: m.UserAccessReport }))
);
const UserVisitsDashboard = lazy(() =>
  import('src/features/admin-console/dev-console/kpi-dashboards/user-visits/UserVisitsDashboard').then((m) => ({ default: m.UserVisitsDashboard }))
);
const APIStats = lazy(() =>
  import('src/features/admin-console/dev-console/kpi-dashboards/api-stats/APIStats').then((m) => ({ default: m.APIStats }))
);
const EmbeddedDashboards = lazy(() =>
  import('src/features/admin-console/dev-console/kpi-dashboards/embedded-dashboards/EmbeddedDashboards').then((m) => ({
    default: m.EmbeddedDashboards
  }))
);
const GlobalAccessManagement = lazy(() => import('src/features/admin-console/access-management/GlobalAccessManagement'));

// Access control wrappers (lazy — only needed when navigating to admin routes)
const XptFinanceAdminWrapper = lazy(() => import('src/features/admin-console/XPTAdminWrapper').then((m) => ({ default: m.XptFinanceAdminWrapper })));
const XPTOperationsAdminWrapper = lazy(() =>
  import('src/features/admin-console/XPTAdminWrapper').then((m) => ({ default: m.XPTOperationsAdminWrapper }))
);
const XPTDevOnlyWrapper = lazy(() => import('src/features/admin-console/XPTAdminWrapper').then((m) => ({ default: m.XPTDevOnlyWrapper })));

/**
 * Renders nothing — activates the parent message listener for cross-frame
 * communication when xPT is embedded inside FinSuite's iframe.
 * Must be rendered inside BrowserRouter to access useNavigate().
 */
const ParentMessageListener: React.FC = () => {
  useParentMessageListener();
  return null;
};

export const AppRouter: React.FC = () => {
  return (
    <div data-testid="app-router">
      <BrowserRouter>
        <ParentMessageListener />
        <XPTGlobalTopNavigation />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<LoadingSpinner />}>
            <AuthWrapper>
              <XptApplicationLevelBanner />
              <Routes>
                {/* Landing Page */}
                <Route path="/" element={<XptHomePage />} />

                {/* Self-service access request (feature-flagged) */}
                <Route element={<AccessRequestFeatureGate />}>
                  <Route path="/request-access" element={<RequestAccessPage />} />
                  <Route path="/my-requests" element={<MyRequestsPage />} />
                </Route>

                {/* Business Group Routes */}
                <Route path="/:dataClassificationShortDesc" element={<XPTBusinessGroupWrapper />}>
                  <Route index element={<ForecastTemplateMainPage />} />

                  {/* Routes for Finance Admins or Finance Owners */}
                  <Route element={<XptFinanceAdminOrFinanceOwnerWrapper />}>
                    <Route path="access-authorization" element={<AccessAuthorization />} />
                    <Route path="planning-cycle" element={<PlanningCycleHomePageNonAdmin />} />
                    <Route path="snapshots" element={<SnapshotsHomePage />} />
                    <Route path="budget-owner-transfer" element={<BudgetOwnerTransfer />} />
                    <Route path="expense-type" element={<ExpenseTypeMapping />} />
                    <Route path="useful-life" element={<UsefulLifeMapping />} />
                  </Route>

                  {/* xVT Planning routes (HDW-only; stage-specific role/cost-center gate via XptXvtAccessWrapper) */}
                  <Route element={<XptXvtAccessWrapper />}>
                    <Route path="xvt-forecast-input" element={<XvtForecastInputMainPage />} />
                    <Route path="xvt-snapshots" element={<XvtSnapshotsHomePage />} />
                    <Route path="xvt-reports" element={<XvtReportsHomePage />} />
                  </Route>

                  {/* Routes accessible to other roles */}
                  <Route path="forecast-input" element={<ForecastTemplateMainPage />} />
                  <Route path="po-tagging" element={<POTagging />} />
                  <Route path="campaign" element={<CampaignsHomePage />} />
                  <Route path="po-mapping" element={<DmmInvoiceMappingHomePage />} />
                  <Route path="channel-funnel-mapping" element={<DmmChannelFunnelMappingHomePage />} />
                  <Route path="forecast-report" element={<XptForecastReport />} />
                  <Route path="variance-report" element={<XptVarianceReport />} />
                  <Route path="actuals-vs-forecast-report" element={<ActualsVsForecastReport />} />
                  <Route path="actuals-report" element={<XptActualsReport />} />
                  <Route path="dmm-marketing-spend" element={<DmmMarketingSpend />} />
                  <Route path="budget-tracker" element={<RemainingBudgetReport />} />
                  <Route path="report-builder" element={<XptReportBuilder />} />
                </Route>

                {/* Admin Console Routes */}
                <Route path="/admin-console" element={<XptFinanceAdminWrapper />}>
                  <Route index element={<PlanningCycleHomePageAdmin />} />
                  <Route path="planning-cycle" element={<PlanningCycleHomePageAdmin />}>
                    <Route path="manage" element={<PlanningCycleManageFormAdmin />} />
                  </Route>

                  {/* CapEx route with Operations Admin access control */}
                  <Route element={<XPTOperationsAdminWrapper />}>
                    <Route path="capex" element={<CapexFileUpload />} />
                  </Route>

                  <Route path="project" element={<ProjectMapping />} />

                  {/* Developer-only Routes */}
                  <Route element={<XPTDevOnlyWrapper />}>
                    <Route path="onboarding" element={<OnboardingHomePage />}>
                      <Route path=":id" element={<OnboardingForm />} />
                    </Route>
                  </Route>

                  <Route path="audit-log" element={<XPTDevOnlyWrapper />}>
                    <Route index element={<AuditLog />} />
                  </Route>

                  <Route path="user-access-report" element={<XPTDevOnlyWrapper />}>
                    <Route index element={<UserAccessReport />} />
                  </Route>

                  <Route path="user-visits" element={<XPTDevOnlyWrapper />}>
                    <Route index element={<UserVisitsDashboard />} />
                  </Route>

                  <Route path="api-stats" element={<XPTDevOnlyWrapper />}>
                    <Route index element={<APIStats />} />
                  </Route>

                  <Route path="user-metrics" element={<XPTDevOnlyWrapper />}>
                    <Route index element={<EmbeddedDashboards />} />
                  </Route>

                  <Route path="access-management" element={<AccessRequestFeatureGate />}>
                    <Route index element={<GlobalAccessManagement />} />
                  </Route>
                </Route>

                {/* Fallback Route */}
                <Route path="*" element={<PageNotFound />} />
              </Routes>
              <XPTFooter />
            </AuthWrapper>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
};
